import { useEffect, useRef, type ReactNode } from "react";
import { useTranslation } from "@repo/i18n";
import { applyVenueTheme, type VenueBranding } from "../theme";
import { LanguageControl } from "./LanguageControl";

export interface ShellFrameProps {
  /** Venue display name for the header; falls back to the generic CTA while config loads. */
  venueName?: string;
  /** Optional venue logo (standalone/bio-link reassurance header) — absent → a clean name-only header. */
  logoUrl?: string;
  branding?: VenueBranding | null;
  children: ReactNode;
}

/**
 * The widget chrome every shell state renders inside: a themeable surface (venue branding → `--rsv-*`
 * custom properties applied to the root), a header carrying the venue logo + name + the in-place
 * language switch, and a `<main>` for the state/outlet. One venue per mount — there is NO venue
 * switcher (features/booking-widget-embed.md R5). Sized to work at ≥320px with no horizontal clip.
 *
 * The venue NAME is venue-authored content and always French (localization-i18n R6), so it carries an
 * explicit `lang="fr"` — under EN chrome (document `lang="en"`) this keeps a screen reader pronouncing
 * it correctly (WCAG 3.1.2). The generic fallback ("Réserver"/"Book") is CHROME copy and stays in the
 * document language.
 */
export function ShellFrame({ venueName, logoUrl, branding, children }: ShellFrameProps) {
  const { t } = useTranslation();
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (rootRef.current) applyVenueTheme(rootRef.current, branding);
  }, [branding]);

  return (
    <div className="rsv-root" ref={rootRef} data-rsv-root>
      <header className="rsv-header">
        <div className="rsv-brand">
          {logoUrl ? <img className="rsv-logo" src={logoUrl} alt="" /> : null}
          <h1 className="rsv-title">
            {venueName ? <span lang="fr">{venueName}</span> : t("widget.book")}
          </h1>
        </div>
        <LanguageControl />
      </header>
      <main className="rsv-main">{children}</main>
    </div>
  );
}
